import React, { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Grid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridFilterChangeEvent,
  GridColumnMenuSort,
  GridColumnMenuCheckboxFilter,
  GridColumnMenuColumnsChooser,
  GridColumnState,
} from "@progress/kendo-react-grid";
import { SortDescriptor, CompositeFilterDescriptor, filterBy as kendoFilterBy } from "@progress/kendo-data-query";
import { getter } from "@progress/kendo-react-common";
import { toast } from "sonner";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import { callApi, HttpMethod } from "src/services/apiService";
import { SHOW_PAYWALLS } from "src/constants/featureFlags";
import Loading from "src/components/Loading";
import { TC } from "./utils/trustCallApi";
import { useActivePhones } from "./hooks/useActivePhones";
import { useAccountConfig, AGENT_READ_ONLY_MESSAGE } from "./hooks/useAccountConfig";
import { usePhoneActions } from "./hooks/usePhoneActions";
import AgentReadOnlyBanner from "./components/AgentReadOnlyBanner";
import PhoneGridToolbar from "./components/PhoneGridToolbar";
import RemediationPaywall from "./RemediationPaywall";
import ScoreCell from "./components/ScoreCell";
import PhoneNumberCell from "./components/PhoneNumberCell";
import FtcCountCell from "./components/FtcCountCell";
import ServiceTypeCell from "./components/ServiceTypeCell";
import LegalEntityCell from "./components/LegalEntityCell";
import AppScoreCell from "./components/AppScoreCell";
import RemediationSwitchModal from "./components/RemediationSwitchModal";
import PhoneErrorModal from "./components/PhoneErrorModal";
import DemoExpiredModal from "./components/DemoExpiredModal";
import AddPhonesSection from "./components/AddPhonesSection";
import BulkAddSection from "./components/BulkAddSection";
import BulkRemoveSection from "./components/BulkRemoveSection";
import { usePhoneQuota } from "./hooks/usePhoneQuota";
import PhoneDetailsModal from "./components/PhoneDetailsModal";
import FtcComplaintsModal from "./components/FtcComplaintsModal";
import AppScoreModal from "./components/AppScoreModal";
import OriginateCallModal from "./components/OriginateCallModal";
import DeletePhoneModal from "./components/DeletePhoneModal";
import RescanModal from "./components/RescanModal";
import TnrmModal from "./components/TnrmModal";
import { getAttLabel } from "./utils/carrierDisplay";
import { formatShortDateTime, parseTsToDate } from "./utils/dateHelpers";
import { RefreshPager, RefreshPagerProvider } from "./components/RefreshPager";
import type { TrustCallPhone, CsvUploadResult } from "./types";

// Use phoneNumber as row identity for selection — always present and unique per account.
// Bulk operations require TrustCallId (database ID), so we map via getTrustCallIds().
const DATA_ITEM_KEY = "phoneNumber";
const idGetter = getter(DATA_ITEM_KEY);

// Selection context — shared between Grid and checkbox cells to avoid
// inline function re-creation that causes Kendo Grid to re-render all rows
const SelectionContext = React.createContext<{
  selectedState: Record<string, boolean>;
  toggle: (key: string) => void;
  toggleAll: () => void;
  allChecked: boolean;
}>({ selectedState: {}, toggle: () => {}, toggleAll: () => {}, allChecked: false });

const CheckboxCell: React.FC<any> = (props) => {
  const { selectedState, toggle } = React.useContext(SelectionContext);
  const key = props.dataItem?.phoneNumber;
  return (
    <td style={{ textAlign: "center" }}>
      <input
        type="checkbox"
        checked={!!selectedState[key]}
        onChange={() => toggle(key)}
      />
    </td>
  );
};

const SelectAllHeader: React.FC = () => {
  const { toggleAll, allChecked } = React.useContext(SelectionContext);
  return (
    <span style={{ display: "block", textAlign: "center" }}>
      <input
        type="checkbox"
        checked={allChecked}
        onChange={() => toggleAll()}
      />
    </span>
  );
};

// Column menu matching MVC Kendo Grid exactly:
//   - Sort Ascending / Sort Descending
//   - Filter → checkbox list with Search box, Filter/Clear buttons
// Every data column uses this same menu. The checkbox filter uses the current
// page data to populate the list (matching MVC behavior where the column menu
// filters are client-side on the loaded dataset).
// Column menu is defined INSIDE the component (below) so it can access the
// column-visibility state for the show/hide columns chooser.

// Edit context — per-row inline editing without re-rendering the entire grid.
// Only the editing row's LabelCell consume the context and update.
const EditContext = React.createContext<{
  editingPhone: string | null;
  editLabel: string;
  isSaving: boolean;
  // DNC-390: via context, not useCanWrite() — the cell renders per row and the grid pages to 1000.
  canWrite: boolean;
  setEditLabel: (value: string) => void;
  onEdit: (phone: TrustCallPhone) => void;
  onUpdate: () => void;
  onCancelEdit: () => void;
  onDelete: (phone: TrustCallPhone) => void;
}>({
  editingPhone: null,
  editLabel: "",
  isSaving: false,
  // false, not true: a cell rendered outside the provider is read-only, not writable.
  canWrite: false,
  setEditLabel: () => {},
  onEdit: () => {},
  onUpdate: () => {},
  onCancelEdit: () => {},
  onDelete: () => {},
});

// Last-updated timestamp, rendered in the same MM/dd/yyyy HH:mm:ss form as the
// Archived grid's "Removed On" column. The API sends an unqualified .NET DateTime
// string, which parseTsToDate handles along with ISO.
const DateLastUpdateCell: React.FC<any> = (props) => (
  <td>{formatShortDateTime(parseTsToDate(props.dataItem?.[props.field ?? ""]))}</td>
);

// Stable cell components that consume EditContext — no inline closures needed in Grid columns
const EditableLabelCell: React.FC<any> = (props) => {
  const { editingPhone, editLabel, isSaving, setEditLabel } = React.useContext(EditContext);
  const isEditing = props.dataItem?.phoneNumber === editingPhone;

  if (isEditing) {
    return (
      <td>
        <input
          type="text"
          className="form-control form-control-sm"
          value={editLabel}
          onChange={(e) => setEditLabel(e.target.value)}
          disabled={isSaving}
          autoFocus
        />
      </td>
    );
  }
  return <td>{props.dataItem?.label}</td>;
};

const EditableActionCell: React.FC<any> = (props) => {
  const { editingPhone, isSaving, canWrite, onEdit, onUpdate, onCancelEdit, onDelete } = React.useContext(EditContext);
  const dataItem = props.dataItem as TrustCallPhone;
  const isEditing = dataItem?.phoneNumber === editingPhone;

  if (isEditing) {
    return (
      <td>
        <div className="d-flex flex-column gap-1">
          <button className="btn btn-primary btn-sm" onClick={onUpdate} disabled={isSaving} style={{ fontSize: "0.75rem" }}>
            {isSaving ? <span className="spinner-border spinner-border-sm me-1" /> : <>&#10003;</>} {isSaving ? "Saving..." : "Update"}
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={onCancelEdit} disabled={isSaving} style={{ fontSize: "0.75rem" }}>
            &#8634; Cancel
          </button>
        </div>
      </td>
    );
  }

  // Compact one-row Edit/Delete — small text + condensed padding, and nowrap so the
  // icon and label stay on the same line within each button.
  const compactBtn = { fontSize: "0.7rem", padding: "1px 6px", whiteSpace: "nowrap" as const };
  return (
    <td>
      <div className="d-flex gap-1">
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => onEdit(dataItem)}
          title={!canWrite ? AGENT_READ_ONLY_MESSAGE : "Edit"}
          disabled={!canWrite}
          style={compactBtn}
        >
          &#9998; Edit
        </button>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() => onDelete(dataItem)}
          title={!canWrite ? AGENT_READ_ONLY_MESSAGE : "Delete"}
          disabled={!canWrite}
          style={compactBtn}
        >
          &#10005; Delete
        </button>
      </div>
    </td>
  );
};

interface ActivePhonesProps {
  section?: "add" | "bulk-add" | "bulk-remove";
}

const ActivePhones: React.FC<ActivePhonesProps> = ({ section }) => {
  const { isSysadmin } = useAppSelector(selectAuth);
  const queryClient = useQueryClient();
  const config = useAccountConfig();
  const canWrite = config.data?.canWrite ?? false;
  const actions = usePhoneActions();
  const quotaQuery = usePhoneQuota();

  // Grid state
  const [page, setPage] = useState({ skip: 0, take: 10 });
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "dateLastUpdate", dir: "desc" },
  ]);
  const [searchString, setSearchString] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [filterBy, setFilterBy] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [ftcFilterDays, setFtcFilterDays] = useState("90");
  const [selectedState, setSelectedState] = useState<Record<string, boolean>>({});
  const [gridFilter, setGridFilter] = useState<CompositeFilterDescriptor | null>(null);

  // Derived config
  const isCanadian = config.data?.isCanadian ?? false;
  const isTrustCallV3 = config.data?.isTrustCallVersion3 ?? false;
  const allowMultipleLegalEntities = config.data?.allowMultipleLegalEntities ?? false;
  const serviceTypes = config.data?.serviceTypes ?? [];
  const legalEntities = config.data?.legalEntities ?? [];

  // Fetch filter options (area codes and states from account's data — matches MVC GetTrustCallFilters)
  const [filterOptions, setFilterOptions] = useState<{ areaCodes: string[]; states: string[] }>({
    areaCodes: [], states: [],
  });
  React.useEffect(() => {
    callApi(`${TC.PHONES_FILTERS}?ftcFilterDays=${ftcFilterDays}`, HttpMethod.GET)
      .then((data: any) => {
        setFilterOptions({
          areaCodes: data.areaCodes ?? data.uniqueArecodes ?? [],
          states: data.states ?? [],
        });
      })
      .catch(() => {});
  }, [ftcFilterDays]);

  // Fetch data
  const sortField = sort[0]?.field || "dateLastUpdate";
  const sortDir = sort[0]?.dir || "desc";

  // Toolbar search/filter → server-side (API query params).
  // When phone search is active, pass filterBy="phone" so the backend filters
  // by phoneNumber/phoneNumberClean (matches MVC getActivePhoneNumbers behavior).
  // When a dropdown filter is active, pass filterBy + filterValue as searchString.
  const effectiveFilterBy = (activeSearch ? "phone" : undefined)
    || filterBy || undefined;
  const effectiveSearchString = activeSearch || filterValue || undefined;
  const phonesQuery = useActivePhones({
    page: Math.floor(page.skip / page.take) + 1,
    pageSize: page.take,
    sortBy: sortField,
    direction: sortDir,
    searchString: effectiveSearchString,
    filterBy: effectiveFilterBy,
    ftcFilterDays,
  });

  const serverData = phonesQuery.data?.data ?? [];
  const total = phonesQuery.data?.total ?? 0;

  // Column show/hide state (keyed by column field). Drives the column-chooser in
  // each column's menu and the `hidden` prop on each data column.
  // Historical is hidden by default to give the remaining columns more room
  // (still toggleable via any column's menu → Columns chooser).
  const [hiddenCols, setHiddenCols] = useState<Record<string, boolean>>({ histScamScore: true });

  // The toggleable data columns offered in the chooser (respects feature flags;
  // the checkbox + actions columns are intentionally not hideable).
  const columnsState: GridColumnState[] = [
    { id: "phoneNumber", field: "phoneNumber", title: "Number" },
    { id: "label", field: "label", title: "Label" },
    ...(allowMultipleLegalEntities
      ? [{ id: "legalName", field: "legalName", title: "Legal Entity" }]
      : []),
    { id: "scamScore", field: "scamScore", title: "Current" },
    { id: "histScamScore", field: "histScamScore", title: "Historical" },
    ...(isTrustCallV3
      ? [
          { id: "verizonScore", field: "verizonScore", title: "Verizon" },
          { id: "attScore", field: "attScore", title: getAttLabel(isCanadian) },
          { id: "tmobileScore", field: "tmobileScore", title: "T-Mobile" },
        ]
      : []),
    { id: "region", field: "region", title: "Region" },
    { id: "locale", field: "locale", title: "Locale" },
    { id: "ftcCount", field: "ftcCount", title: "FTC Complaints" },
    { id: "appScore", field: "appScore", title: "App Score" },
    { id: "serviceType", field: "serviceType", title: "Service" },
    { id: "dateLastUpdate", field: "dateLastUpdate", title: "Last Updated" },
  ].map((c) => ({ ...c, hidden: !!hiddenCols[c.id] }));

  const handleColumnsStateChange = (cols: GridColumnState[]) => {
    setHiddenCols((prev) => {
      const next = { ...prev };
      cols.forEach((c) => {
        next[c.id] = !!c.hidden;
      });
      return next;
    });
  };

  // Column menu: Sort + checkbox Filter + Columns chooser (show/hide). Defined
  // here so it can close over the column-visibility state.
  const ColumnMenu = (props: any) => (
    <div>
      <GridColumnMenuSort {...props} />
      <GridColumnMenuCheckboxFilter {...props} data={props.data} expanded={true} />
      <GridColumnMenuColumnsChooser
        {...props}
        columnsState={columnsState}
        onColumnsStateChange={handleColumnsStateChange}
      />
    </div>
  );

  // Column menu filter → client-side on loaded page data (matches MVC behavior
  // where the column menu checkbox filter operates on the grid's loaded DataSource).
  const data = gridFilter ? kendoFilterBy(serverData, gridFilter) : serverData;

  // Selection helpers — selectedPhoneNumbers are the selection keys (formatted phone numbers).
  // getTrustCallIds() maps them to database IDs for bulk API operations.
  const selectedPhoneNumbers = Object.keys(selectedState).filter((k) => selectedState[k]);
  const selectedCount = selectedPhoneNumbers.length;
  const getTrustCallIds = (): string[] =>
    data
      .filter((item) => selectedState[item.phoneNumber])
      .map((item) => String(item.id));
  const allChecked = data.length > 0 && data.every((item) => !!selectedState[idGetter(item)]);

  const toggleSelection = React.useCallback((key: string) => {
    setSelectedState((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleAllSelection = React.useCallback(() => {
    setSelectedState((prev) => {
      const allCurrentlyChecked = data.every((item) => !!prev[idGetter(item)]);
      const newState = { ...prev };
      data.forEach((item) => {
        newState[idGetter(item)] = !allCurrentlyChecked;
      });
      return newState;
    });
  }, [data]);

  const selectionContextValue = React.useMemo(() => ({
    selectedState, toggle: toggleSelection, toggleAll: toggleAllSelection, allChecked,
  }), [selectedState, toggleSelection, toggleAllSelection, allChecked]);

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage(event.page);
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
    // Reset to first page when sort changes (matches MVC behavior)
    setPage({ skip: 0, take: page.take });
  };

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setGridFilter(event.filter);
  };

  // Search and filter are mutually exclusive (matches MVC behavior).
  // Clicking Search clears the filter; selecting a filter clears the search.
  const handleSearch = () => {
    setGridFilter(null);
    setFilterBy("");
    setFilterValue("");
    setActiveSearch(searchString);
    setPage({ skip: 0, take: page.take });
  };

  const handleSearchChange = (value: string) => {
    setSearchString(value);
    // When search textbox is cleared, reset and reload (matches MVC .change handler)
    if (value === "") {
      setActiveSearch("");
      setFilterBy("");
      setFilterValue("");
      setPage({ skip: 0, take: page.take });
    }
  };

  // Main filter dropdown changed — clear search, reset sub-filter value
  const handleFilterByChange = (value: string) => {
    setGridFilter(null);
    setSearchString("");
    setActiveSearch("");
    setFilterValue("");
    setFilterBy(value);
    setPage({ skip: 0, take: page.take });
  };

  // Sub-filter value selected — clear search, reload grid (matches MVC .dropDownFilterOption handler)
  const handleFilterValueChange = (value: string) => {
    setSearchString("");
    setActiveSearch("");
    setFilterValue(value);
    setPage({ skip: 0, take: page.take });
  };

  // FTC days changed — reset all filters and reload (matches MVC .dropDownFilterOptionFTC handler)
  const handleFtcFilterDaysChange = (value: string) => {
    setSearchString("");
    setActiveSearch("");
    setFilterBy("");
    setFilterValue("");
    setFtcFilterDays(value);
    setPage({ skip: 0, take: page.take });
  };

  const handleResetFilter = () => {
    setSearchString("");
    setActiveSearch("");
    setFilterBy("");
    setFilterValue("");
    setFtcFilterDays("90");
    setPage({ skip: 0, take: page.take });
  };

  // Track which phone has a pending service type update for inline loader
  const [serviceTypeUpdatingPhone, setServiceTypeUpdatingPhone] = useState<string | null>(null);
  const [legalEntityUpdatingPhone, setLegalEntityUpdatingPhone] = useState<string | null>(null);

  // Inline edit handlers
  // MVC controller strips non-digits before calling domain (Regex("[^0-9]").Replace).
  // React must pass the clean number since it's used as a URL path parameter.
  const cleanPhoneNumber = (formatted: string): string => {
    const phone = data.find((p) => p.phoneNumber === formatted);
    return phone?.phoneNumberClean || formatted.replace(/\D/g, "");
  };

  const handleServiceTypeChange = (phoneNumber: string, serviceType: string, revert: () => void) => {
    // If switching FROM Remediation/DailyScans TO Scanning, show confirmation
    const phone = data.find((p) => p.phoneNumber === phoneNumber);
    const currentType = phone?.serviceType ?? "";
    const isDowngrading =
      (currentType === "Remediation" || currentType === "Remediation Daily Scans") &&
      serviceType === "Scanning";

    if (isDowngrading) {
      setRemediationSwitch({ phoneNumber, newType: serviceType });
      // Revert dropdown immediately — will be re-applied if user confirms
      revert();
    } else {
      setServiceTypeUpdatingPhone(phoneNumber);
      actions.updateServiceType.mutate(
        { phoneNumber: cleanPhoneNumber(phoneNumber), serviceType },
        {
          onSettled: () => setServiceTypeUpdatingPhone(null),
          onError: () => revert(),
        }
      );
    }
  };

  const handleLegalEntityChange = (phoneNumber: string, legalEntityId: string, revert: () => void) => {
    setLegalEntityUpdatingPhone(phoneNumber);
    actions.updateLegalEntity.mutate(
      { phoneNumber: cleanPhoneNumber(phoneNumber), legalEntityId: Number(legalEntityId) },
      {
        onSettled: () => setLegalEntityUpdatingPhone(null),
        onError: () => revert(),
      }
    );
  };

  // Delete modal state — single delete stores formatted phone number, bulk stores null
  const [deleteTarget, setDeleteTarget] = useState<{ phoneNumber: string; phoneNumberClean: string } | null>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  // Rescan modal state
  const [showRescanModal, setShowRescanModal] = useState(false);
  // TNRM modal state — null when closed, "add" or "remove" when open
  const [tnrmMode, setTnrmMode] = useState<"add" | "remove" | null>(null);

  const handleDeletePhone = (phone: TrustCallPhone) => {
    setDeleteTarget({ phoneNumber: phone.phoneNumber, phoneNumberClean: phone.phoneNumberClean || phone.phoneNumber.replace(/\D/g, "") });
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  const confirmSingleDelete = () => {
    if (deleteTarget) {
      actions.deletePhone.mutate(deleteTarget.phoneNumberClean, {
        onSettled: () => setDeleteTarget(null),
      });
    }
  };

  const confirmBulkDelete = () => {
    actions.bulkDeletePhones.mutate(getTrustCallIds(), {
      onSettled: () => {
        setSelectedState({});
        setShowBulkDeleteModal(false);
      },
    });
  };

  const handleRescan = () => {
    setShowRescanModal(true);
  };

  const confirmRescan = () => {
    actions.rescanPhones.mutate(getTrustCallIds(), {
      onSettled: () => {
        setSelectedState({});
        setShowRescanModal(false);
      },
    });
  };

  const handleAddTnrm = () => {
    setTnrmMode("add");
  };

  const handleRemoveTnrm = () => {
    setTnrmMode("remove");
  };

  const confirmTnrm = () => {
    const ids = getTrustCallIds();
    const mutation = tnrmMode === "add" ? actions.bulkAddTnrm : actions.bulkRemoveTnrm;
    mutation.mutate(ids, {
      onSettled: () => {
        setSelectedState({});
        setTnrmMode(null);
      },
    });
  };

  const handleExportCsv = () => {
    // Trigger file download via window.open
    const apiUrl = import.meta.env.VITE_APP_API_URL;
    window.open(`${apiUrl}/trustcall/export/active-csv`, "_blank");
  };

  // Learn About Remediation — the paywall makes the account-manager request that
  // the MVC #learnAboutRemediation button fired directly. Production still fires
  // it directly until the paywall launches.
  const [showRemediationPaywall, setShowRemediationPaywall] = useState(false);
  const [remediationSent, setRemediationSent] = useState(false);

  const handleLearnRemediation = async () => {
    try {
      const result = await callApi(TC.PHONES_SEND_REMEDIATION, HttpMethod.POST, { totalActivePhoneCount: total });
      if (result?.isSuccess) {
        setRemediationSent(true);
        toast.info("Your account manager will contact you with information about remediation", { duration: 150000, closeButton: true });
      }
    } catch {
      toast.error("Failed to send remediation inquiry");
    }
  };

  // Inline edit state (matches MVC Kendo InLine edit mode — only label is editable)
  const [editingPhone, setEditingPhone] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = (phone: TrustCallPhone) => {
    setEditingPhone(phone.phoneNumber);
    setEditLabel(phone.label ?? "");
  };

  // Matches MVC flow: POST update → grid dataSource.read() → exit edit mode.
  // The spinner stays visible until the grid is refreshed with server data.
  const handleUpdate = async () => {
    if (editingPhone !== null) {
      const phone = data.find((p) => p.phoneNumber === editingPhone);
      const cleanNumber = phone?.phoneNumberClean || editingPhone.replace(/\D/g, "");
      setIsSaving(true);
      try {
        await actions.updateLabel.mutateAsync({ phoneNumber: cleanNumber, label: editLabel });
        // Wait for the grid data to refresh from server before closing edit mode
        await queryClient.invalidateQueries({ queryKey: ["trustcall", "phones"] });
      } catch {
        // Error toast handled by mutation onError
      } finally {
        setIsSaving(false);
        setEditingPhone(null);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingPhone(null);
    setEditLabel("");
  };

  // No useMemo — avoids stale closures where handleUpdate captures old state.
  // Context consumers (EditableLabelCell, EditableActionCell) are defined outside
  // the component so Kendo Grid won't remount them on re-render.
  const editContextValue = {
    editingPhone, editLabel, isSaving, canWrite, setEditLabel,
    onEdit: handleEdit, onUpdate: handleUpdate, onCancelEdit: handleCancelEdit,
    onDelete: handleDeletePhone,
  };

  // Modal state
  const [showOriginateCall, setShowOriginateCall] = useState(false);
  const [detailsPhone, setDetailsPhone] = useState<TrustCallPhone | null>(null);
  const [ftcPhone, setFtcPhone] = useState<TrustCallPhone | null>(null);
  const [scorePhone, setScorePhone] = useState<TrustCallPhone | null>(null);
  const [bulkAddResult, setBulkAddResult] = useState<CsvUploadResult | null>(null);
  const [bulkRemoveResult, setBulkRemoveResult] = useState<CsvUploadResult | null>(null);
  const [remediationSwitch, setRemediationSwitch] = useState<{ phoneNumber: string; newType: string } | null>(null);
  const [phoneErrors, setPhoneErrors] = useState<{ phone: string; status: string }[]>([]);

  const isNXXGCrossAccount = config.data?.isNXXGCrossAccount ?? false;

  // Modal handlers
  const handleOriginateCall = () => setShowOriginateCall(true);
  const handlePhoneDetails = (phone: TrustCallPhone) => setDetailsPhone(phone);
  const handleFtcDetails = (phone: TrustCallPhone) => setFtcPhone(phone);
  const handleAppScoreDetails = (phone: TrustCallPhone) => setScorePhone(phone);

  const handleBulkAddUpload = (file: File, st: number, leId: number | undefined, updateExisting: boolean) => {
    setBulkAddResult(null);
    actions.uploadPhonesCsv.mutate(
      { file, serviceType: st, legalEntityId: leId, updateExisting },
      {
        onSuccess: (result) => setBulkAddResult(result as unknown as CsvUploadResult),
        onError: () => setBulkAddResult(null),
      }
    );
  };

  const handleBulkRemoveUpload = (file: File) => {
    setBulkRemoveResult(null);
    actions.bulkDeleteUpload.mutate(file, {
      onSuccess: (result) => setBulkRemoveResult(result as unknown as CsvUploadResult),
      onError: () => setBulkRemoveResult(null),
    });
  };

  const selectedPhoneForOriginate = data.find((p) => selectedState[idGetter(p)]);

  const handleRefresh = useCallback(() => { void phonesQuery.refetch(); }, [phonesQuery.refetch]);
  const refreshPagerValue = useMemo(
    () => ({ onRefresh: handleRefresh, isFetching: phonesQuery.isFetching }),
    [handleRefresh, phonesQuery.isFetching],
  );

  // DNC-390: a user without trustcall_write sees these three pages in the sidebar and can
  // open them, but may not submit. A <fieldset disabled> greys out every control inside —
  // inputs, selects, the hidden-file-input trigger buttons — without threading a
  // flag into each section component, and it covers controls added later too.
  // (Bootstrap's reboot strips the fieldset's own border/margin, so no visual
  // change when writes are allowed.)
  // Reads inside these sections must be anchors — see BulkAddSection's Download link.
  const readOnlyWithoutWrite = (form: React.ReactNode) => (
    <>
      <AgentReadOnlyBanner />
      <fieldset disabled={!canWrite}>{form}</fieldset>
    </>
  );

  // Show section content if sidebar navigated to a section
  if (section === "add") {
    return readOnlyWithoutWrite(
      <AddPhonesSection
        serviceTypes={serviceTypes}
        legalEntities={legalEntities}
        isSysadmin={isSysadmin ?? false}
        allowMultipleLegalEntities={allowMultipleLegalEntities}
        onAddPhones={(phones) => actions.addPhones.mutateAsync(phones)}
        isSubmitting={actions.addPhones.isPending}
        quota={quotaQuery.data}
        isOnlySysadminAllowedToAddTCNumber={config.data?.isOnlySysadminAllowedToAddTCNumber ?? true}
        isDemo={config.data?.isDemo ?? false}
        remainingCredits={config.data?.remainingCredits ?? 11}
      />
    );
  }
  if (section === "bulk-add") {
    return readOnlyWithoutWrite(
      <BulkAddSection
        serviceTypes={serviceTypes}
        legalEntities={legalEntities}
        isSysadmin={isSysadmin ?? false}
        allowMultipleLegalEntities={allowMultipleLegalEntities}
        onUpload={handleBulkAddUpload}
        isUploading={actions.uploadPhonesCsv.isPending}
        uploadResult={bulkAddResult}
        quota={quotaQuery.data}
        isDemo={config.data?.isDemo ?? false}
        remainingCredits={config.data?.remainingCredits ?? 0}
      />
    );
  }
  if (section === "bulk-remove") {
    return readOnlyWithoutWrite(
      <BulkRemoveSection
        onUpload={handleBulkRemoveUpload}
        isUploading={actions.bulkDeleteUpload.isPending}
        uploadResult={bulkRemoveResult}
      />
    );
  }

  return (
    <div>
      <AgentReadOnlyBanner />
      <PhoneGridToolbar
        searchString={searchString}
        onSearchChange={handleSearchChange}
        onSearch={handleSearch}
        filterBy={filterBy}
        onFilterByChange={handleFilterByChange}
        filterValue={filterValue}
        onFilterValueChange={handleFilterValueChange}
        ftcFilterDays={ftcFilterDays}
        onFtcFilterDaysChange={handleFtcFilterDaysChange}
        onResetFilter={handleResetFilter}
        filterOptions={filterOptions}
        hasRemediationPlan={config.data && (config.data.hasRemediationDefault || config.data.hasRemediationDailyScanDefault)}
        selectedCount={selectedCount}
        isSysadmin={isSysadmin ?? false}
        isTrustCallV3={isTrustCallV3}
        canWrite={canWrite}
        onDelete={handleBulkDelete}
        onRescan={handleRescan}
        onAddTnrm={handleAddTnrm}
        onRemoveTnrm={handleRemoveTnrm}
        onOriginateCall={handleOriginateCall}
        onExportCsv={handleExportCsv}
        onLearnRemediation={
          SHOW_PAYWALLS
            ? () => setShowRemediationPaywall(true)
            : handleLearnRemediation
        }
        isLearnRemediationDisabled={remediationSent}
      />

      {SHOW_PAYWALLS && showRemediationPaywall && (
        <RemediationPaywall
          activePhoneCount={total}
          isSent={remediationSent}
          onSent={() => setRemediationSent(true)}
          onClose={() => setShowRemediationPaywall(false)}
        />
      )}

      <div style={{ position: "relative" }}>
        {phonesQuery.isFetching && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}>
            <Loading />
          </div>
        )}
        <EditContext.Provider value={editContextValue}>
        <SelectionContext.Provider value={selectionContextValue}>
        <RefreshPagerProvider value={refreshPagerValue}>
        <Grid
          data={data}
          total={total}
          pageable={{
            buttonCount: 5,
            pageSizes: [10, 25, 50, 100, 1000],
          }}
          pager={RefreshPager}
          skip={page.skip}
          take={page.take}
          onPageChange={handlePageChange}
          sortable={{ mode: "single", allowUnsort: true }}
          sort={sort}
          onSortChange={handleSortChange}
          filter={gridFilter ?? undefined}
          onFilterChange={handleFilterChange}
          resizable={true}
          reorderable={true}
          dataItemKey={DATA_ITEM_KEY}
          // Fixed height keeps the header pinned at the top and the pager pinned at
          // the bottom (best-in-class grid UX) while the body (.k-grid-content)
          // scrolls. Every column has an explicit width, which keeps Kendo's
          // separate header/body tables column-aligned in scrollable mode.
          style={{ height: "calc(100vh - 160px)" }}
        >
        <Column
          title=" "
          width={45}
          sortable={false}
          reorderable={false}
          headerCell={SelectAllHeader}
          cell={CheckboxCell}
        />

        <Column
          field="phoneNumber"
          title="Number"
          width={119}
          hidden={!!hiddenCols.phoneNumber}
          columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
          cell={(cellProps) => (
            <PhoneNumberCell
              {...cellProps}
              isTrustCallV3={isTrustCallV3}
              onDetailsClick={handlePhoneDetails}
            />
          )}
        />

        <Column field="label" title="Label" width={97} hidden={!!hiddenCols.label} columnMenu={(props) => <ColumnMenu {...props} data={serverData} />} cell={EditableLabelCell} />

        {allowMultipleLegalEntities && (
          <Column
            field="legalName"
            title="Legal Entity"
            headerClassName="tc-wrap-header"
            width={101}
            hidden={!!hiddenCols.legalName}
            columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
            cell={(cellProps) => (
              <LegalEntityCell
                {...cellProps}
                legalEntities={legalEntities}
                isLoading={legalEntityUpdatingPhone === cellProps.dataItem.phoneNumber}
                disabled={!canWrite}
                onChange={handleLegalEntityChange}
              />
            )}
          />
        )}

        <Column
          field="scamScore"
          title="Current"
          width={98}
          hidden={!!hiddenCols.scamScore}
          cell={ScoreCell}
          columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
        />

        <Column
          field="histScamScore"
          title="Historical"
          width={90}
          hidden={!!hiddenCols.histScamScore}
          cell={ScoreCell}
          columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
        />

        {isTrustCallV3 && (
          <Column
            field="verizonScore"
            title="Verizon"
            width={100}
            hidden={!!hiddenCols.verizonScore}
            cell={ScoreCell}
            columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
          />
        )}

        {isTrustCallV3 && (
          <Column
            field="attScore"
            title={getAttLabel(isCanadian)}
            width={91}
            hidden={!!hiddenCols.attScore}
            cell={ScoreCell}
            columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
          />
        )}

        {isTrustCallV3 && (
          <Column
            field="tmobileScore"
            title="T-Mobile"
            width={118}
            hidden={!!hiddenCols.tmobileScore}
            cell={ScoreCell}
            columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
          />
        )}

        <Column field="region" title="Region" width={98} hidden={!!hiddenCols.region} columnMenu={(props) => <ColumnMenu {...props} data={serverData} />} />
        <Column field="locale" title="Locale" width={105} hidden={!!hiddenCols.locale} columnMenu={(props) => <ColumnMenu {...props} data={serverData} />} />

        <Column
          field="ftcCount"
          title="FTC Complaints"
          width={131}
          hidden={!!hiddenCols.ftcCount}
          columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
          cell={(cellProps) => (
            <FtcCountCell {...cellProps} onDetailsClick={handleFtcDetails} />
          )}
        />

        <Column
          field="appScore"
          title="App Score"
          width={99}
          hidden={!!hiddenCols.appScore}
          headerClassName="tc-wrap-header"
          columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
          cell={(cellProps) => (
            <AppScoreCell {...cellProps} onDetailsClick={handleAppScoreDetails} />
          )}
        />

        <Column
          field="serviceType"
          title="Service"
          width={112}
          hidden={!!hiddenCols.serviceType}
          columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
          cell={(cellProps) => (
            <ServiceTypeCell
              {...cellProps}
              serviceTypes={serviceTypes}
              isSysadmin={isSysadmin ?? false}
              isLoading={serviceTypeUpdatingPhone === cellProps.dataItem.phoneNumber}
              disabled={!canWrite}
              onChange={handleServiceTypeChange}
            />
          )}
        />

        <Column
          field="dateLastUpdate"
          title="Last Updated"
          width={136}
          hidden={!!hiddenCols.dateLastUpdate}
          headerClassName="tc-wrap-header"
          columnMenu={(props) => <ColumnMenu {...props} data={serverData} />}
          cell={DateLastUpdateCell}
        />

        {/* MVC columns 8, 15-24 are Hidden(true) by default and accessible via column menu.
            Kendo React Grid doesn't support show={false}, so these are not rendered.
            Max Score, Last Check dates, Flagged Since dates, TNRM dates are available
            in the data but not displayed as columns (matching MVC default view). */}

        <Column title="" width={131} cell={EditableActionCell} sortable={false} />

        {/* TNRM and Hiya per-row actions are handled via toolbar bulk buttons,
            not as visible grid columns (matching MVC behavior) */}
        </Grid>
        </RefreshPagerProvider>
        </SelectionContext.Provider>
        </EditContext.Provider>
      </div>

      {/* Modals */}
      <PhoneDetailsModal
        show={!!detailsPhone}
        phone={detailsPhone}
        isCanadian={isCanadian}
        onClose={() => setDetailsPhone(null)}
      />

      <FtcComplaintsModal
        show={!!ftcPhone}
        phone={ftcPhone}
        filterDays={ftcFilterDays}
        onClose={() => setFtcPhone(null)}
      />

      <AppScoreModal
        show={!!scorePhone}
        phone={scorePhone}
        onClose={() => setScorePhone(null)}
      />

      <OriginateCallModal
        show={showOriginateCall}
        phone={selectedPhoneForOriginate ?? null}
        onClose={() => setShowOriginateCall(false)}
      />

      <RemediationSwitchModal
        show={!!remediationSwitch}
        phoneNumber={remediationSwitch?.phoneNumber ?? ""}
        onConfirm={() => {
          if (remediationSwitch) {
            setServiceTypeUpdatingPhone(remediationSwitch.phoneNumber);
            actions.updateServiceType.mutate(
              {
                phoneNumber: cleanPhoneNumber(remediationSwitch.phoneNumber),
                serviceType: remediationSwitch.newType,
              },
              { onSettled: () => setServiceTypeUpdatingPhone(null) }
            );
          }
          setRemediationSwitch(null);
        }}
        onCancel={() => setRemediationSwitch(null)}
      />

      <PhoneErrorModal
        show={phoneErrors.length > 0}
        errors={phoneErrors}
        onClose={() => setPhoneErrors([])}
      />

      <DemoExpiredModal
        show={false /* Backend TrustCallFeatureAttribute handles this via 403 */}
        isNXXGCrossAccount={isNXXGCrossAccount}
        onExit={() => window.location.replace("https://www.dncscrub.com/home")}
      />

      <DeletePhoneModal
        show={!!deleteTarget}
        phoneNumber={deleteTarget?.phoneNumber ?? null}
        isDeleting={actions.deletePhone.isPending}
        onConfirm={confirmSingleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <DeletePhoneModal
        show={showBulkDeleteModal}
        phoneNumber={null}
        bulkCount={selectedCount}
        isDeleting={actions.bulkDeletePhones.isPending}
        onConfirm={confirmBulkDelete}
        onCancel={() => setShowBulkDeleteModal(false)}
      />

      <RescanModal
        show={showRescanModal}
        isRescanning={actions.rescanPhones.isPending}
        onConfirm={confirmRescan}
        onCancel={() => setShowRescanModal(false)}
      />

      <TnrmModal
        show={tnrmMode !== null}
        mode={tnrmMode ?? "add"}
        isProcessing={actions.bulkAddTnrm.isPending || actions.bulkRemoveTnrm.isPending}
        onConfirm={confirmTnrm}
        onCancel={() => setTnrmMode(null)}
      />

      {/* FTC filter days — matching MVC: row with fixed widths */}
      <div className="row" style={{ marginTop: 20 }}>
        <div className="col-sm-3" style={{ width: 230 }}>
          <label style={{ marginTop: 10, fontWeight: "normal" }}>
            Show FTC Complaints in last
          </label>
        </div>
        <div className="col-sm-9" style={{ width: 225 }}>
          <select
            className="form-control form-select"
            style={{ cursor: "pointer" }}
            value={ftcFilterDays}
            onChange={(e) => handleFtcFilterDaysChange(e.target.value)}
          >
            <option value="7">7 days</option>
            <option value="15">15 days</option>
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
          </select>
        </div>
      </div>

      {/* Score definitions — matching MVC Monitor.cshtml below the grid */}
      <hr />
      <div className="row">
        <div className="col-md-12">
          <h5 style={{ fontWeight: "bold" }}>Current Spam Score:</h5>
          <span style={{ marginLeft: 100, display: "block" }}>How carrier networks and call monitoring apps perceived the call now. Returns: Clean, Medium, or High.</span>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <h5 style={{ fontWeight: "bold" }}>Maximum Spam Score:</h5>
          <span style={{ marginLeft: 100, display: "block" }}>The highest spam score the number has had in the last 15 days. Returns Clean, Medium, or High.</span>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <h5 style={{ fontWeight: "bold" }}>Historical Spam Score:</h5>
          <span style={{ marginLeft: 100, display: "block" }}>The number of times a number has been tagged as Spam during the period relative to the total times the number has been scored <br /> (think of this as Spam persistence indicator). Returns a value from 1 to 5. The values are defined as below:</span>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <ul style={{ marginLeft: 100, marginTop: 10 }}>
            <li>Value of 5: At least 50% of times scored, the number had a High SPAM Score</li>
            <li>Value of 4: &lt; 50% of times scored, the number had a high SPAM Score</li>
            <li>Value of 3: At least 50% of times scored, the number had a somewhat high SPAM Score</li>
            <li>Value of 2: &lt; 50% of times scored, the number had a somewhat high SPAM Score</li>
            <li>Value of 1: No issues</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ActivePhones;
